import React, { useEffect, useRef, useState } from "react";
import {
  getApp,
  getApps,
  initializeApp,
} from "firebase/app";
import {
  getMessaging,
  isSupported,
  onMessage,
} from "firebase/messaging";
import "./FoodRadarForegroundNotification.css";

const firebaseConfig = {
  apiKey: "AIzaSyBHc0bM8FIeV14aAfH8mC_SIlQXefuBE8k",
  authDomain: "vakra-orders-received.firebaseapp.com",
  projectId: "vakra-orders-received",
  storageBucket: "vakra-orders-received.firebasestorage.app",
  messagingSenderId: "56991820937",
  appId: "1:56991820937:web:4f470dab790d9eeecf6a58",
};

const DEFAULT_URL = "/food-radar/offers";

const toAbsoluteVakraUrl = (value) => {
  const raw = String(value || "").trim();

  if (!raw) return DEFAULT_URL;

  if (
    raw.startsWith("http://") ||
    raw.startsWith("https://")
  ) {
    return raw;
  }

  return raw.startsWith("/") ? raw : `/${raw}`;
};

const numberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getImageUrl = (data = {}, notification = {}) => {
  const value =
    data.imageUrl ||
    data.image_url ||
    data.notificationImage ||
    data.notification_image ||
    data.offerImage ||
    data.offer_image ||
    data.productImage ||
    data.product_image ||
    notification.image ||
    "";

  if (!value) return "";

  const raw = String(value).trim();

  if (
    raw.startsWith("http://") ||
    raw.startsWith("https://") ||
    raw.startsWith("data:") ||
    raw.startsWith("blob:")
  ) {
    return raw;
  }

  if (raw.startsWith("/")) return raw;
  if (raw.startsWith("uploads/")) return `/${raw}`;

  return `/uploads/${raw}`;
};

const parseForegroundPayload = (payload = {}) => {
  const data =
    payload?.data &&
    typeof payload.data === "object"
      ? payload.data
      : {};

  const notification =
    payload?.notification &&
    typeof payload.notification === "object"
      ? payload.notification
      : {};

  const vendorId =
    numberOrZero(
      data.featuredVendorId ||
        data.featured_vendor_id ||
        data.vendorId ||
        data.vendor_id
    );

  const offerId =
    numberOrZero(
      data.featuredOfferId ||
        data.featured_offer_id ||
        data.offerId ||
        data.offer_id
    );

  const vendorName =
    data.featuredVendorName ||
    data.featured_vendor_name ||
    data.vendorName ||
    data.vendor_name ||
    "VAKRA Food Radar";

  const offerName =
    data.featuredOfferName ||
    data.featured_offer_name ||
    data.offerName ||
    data.offer_name ||
    data.body ||
    notification.body ||
    "Aapke liye special food pick";

  const price =
    numberOrZero(
      data.featuredPrice ||
        data.featured_price ||
        data.offerPrice ||
        data.offer_price ||
        data.comboPrice ||
        data.combo_price
    );

  const actualPrice =
    numberOrZero(
      data.actualPrice ||
        data.actual_price
    );

  const discountPercent =
    actualPrice > price && price > 0
      ? Math.round(
          ((actualPrice - price) / actualPrice) * 100
        )
      : numberOrZero(
          data.discountPercent ||
            data.discount_percent
        );

  const deepLink =
    toAbsoluteVakraUrl(
      data.deepLink ||
        data.deep_link ||
        data.click_action ||
        data.link ||
        (vendorId
          ? `/food-radar/offers?vendorId=${vendorId}${
              offerId ? `&offerId=${offerId}` : ""
            }&fromNotification=1`
          : DEFAULT_URL)
    );

  const menuLink =
    vendorId
      ? `/food-radar/order/${vendorId}${
          offerId ? `?offerId=${offerId}` : ""
        }`
      : "/food-radar";

  return {
    title:
      data.title ||
      notification.title ||
      "🔥 Aaj ka Food Pick",
    body:
      data.body ||
      notification.body ||
      "",
    imageUrl:
      getImageUrl(data, notification),
    vendorId,
    offerId,
    vendorName,
    offerName,
    price,
    actualPrice,
    discountPercent,
    deepLink,
    menuLink,
    queueId:
      data.queueId ||
      data.queue_id ||
      "",
  };
};

const playFoodRadarPing = async () => {
  try {
    const AudioContextClass =
      window.AudioContext ||
      window.webkitAudioContext;

    if (!AudioContextClass) return;

    const context = new AudioContextClass();

    if (context.state === "suspended") {
      await context.resume();
    }

    const now = context.currentTime;

    const tones = [
      { start: 0, freq: 880, duration: 0.14 },
      { start: 0.19, freq: 1046, duration: 0.14 },
      { start: 0.39, freq: 1318, duration: 0.22 },
    ];

    tones.forEach((tone) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();

      oscillator.type = "sine";
      oscillator.frequency.value = tone.freq;

      gain.gain.setValueAtTime(
        0.0001,
        now + tone.start
      );

      gain.gain.exponentialRampToValueAtTime(
        0.16,
        now + tone.start + 0.02
      );

      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        now + tone.start + tone.duration
      );

      oscillator.connect(gain);
      gain.connect(context.destination);

      oscillator.start(now + tone.start);
      oscillator.stop(
        now + tone.start + tone.duration + 0.03
      );
    });

    window.setTimeout(() => {
      context.close().catch(() => {});
    }, 1300);
  } catch (error) {
    console.warn(
      "[FOOD RADAR] Foreground ping blocked:",
      error?.message || error
    );
  }
};

function FoodRadarForegroundNotification() {
  const [pick, setPick] = useState(null);
  const lastMessageKeyRef = useRef("");

  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    const startListener = async () => {
      try {
        const supported =
          await isSupported();

        if (!supported || cancelled) {
          return;
        }

        const app =
          getApps().length > 0
            ? getApp()
            : initializeApp(firebaseConfig);

        const messaging =
          getMessaging(app);

        unsubscribe = onMessage(
          messaging,
          (payload) => {
            const parsed =
              parseForegroundPayload(payload);

            const messageKey = [
              parsed.queueId,
              parsed.vendorId,
              parsed.offerId,
              parsed.title,
            ].join(":");

            if (
              messageKey &&
              messageKey ===
                lastMessageKeyRef.current
            ) {
              return;
            }

            lastMessageKeyRef.current =
              messageKey;

            setPick(parsed);

            playFoodRadarPing();

            if (
              typeof navigator !== "undefined" &&
              typeof navigator.vibrate ===
                "function"
            ) {
              navigator.vibrate([
                180,
                90,
                180,
                90,
                260,
              ]);
            }
          }
        );
      } catch (error) {
        console.warn(
          "[FOOD RADAR] Foreground notification listener skipped:",
          error?.message || error
        );
      }
    };

    startListener();

    return () => {
      cancelled = true;

      try {
        unsubscribe();
      } catch {
        // no-op
      }
    };
  }, []);

  useEffect(() => {
    if (!pick) return undefined;

    const oldOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    return () => {
      document.body.style.overflow =
        oldOverflow;
    };
  }, [pick]);

  if (!pick) {
    return null;
  }

  const goTo = (url) => {
    if (!url) return;

    setPick(null);
    window.location.href = url;
  };

  return (
    <div
      className="fr-fg-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="New Food Radar offer"
    >
      <div className="fr-fg-popup">
        <button
          type="button"
          className="fr-fg-close"
          onClick={() => setPick(null)}
          aria-label="Close"
        >
          ×
        </button>

        <div className="fr-fg-bell-wrap">
          <div className="fr-fg-bell-wave left" />
          <div className="fr-fg-bell">🔔</div>
          <div className="fr-fg-bell-wave right" />
        </div>

        <div className="fr-fg-pick-label">
          🔥 Aaj ka Food Pick
        </div>

        <div className="fr-fg-image-wrap">
          {pick.imageUrl ? (
            <img
              src={pick.imageUrl}
              alt={pick.offerName}
              className="fr-fg-image"
              onError={(event) => {
                event.currentTarget.style.display =
                  "none";

                const fallback =
                  event.currentTarget
                    .parentElement
                    ?.querySelector(
                      ".fr-fg-image-fallback"
                    );

                if (fallback) {
                  fallback.style.display =
                    "grid";
                }
              }}
            />
          ) : null}

          <div
            className="fr-fg-image-fallback"
            style={{
              display: pick.imageUrl
                ? "none"
                : "grid",
            }}
          >
            🍽️
          </div>
        </div>

        <div className="fr-fg-copy">
          <span className="fr-fg-vendor">
            {pick.vendorName}
          </span>

          <h2>
            {pick.offerName}
          </h2>

          {pick.price > 0 && (
            <div className="fr-fg-price-row">
              <strong>
                ₹{pick.price.toLocaleString(
                  "en-IN"
                )}
              </strong>

              {pick.actualPrice >
                pick.price && (
                <del>
                  ₹
                  {pick.actualPrice.toLocaleString(
                    "en-IN"
                  )}
                </del>
              )}

              {pick.discountPercent > 0 && (
                <span>
                  {pick.discountPercent}% OFF
                </span>
              )}
            </div>
          )}

          {pick.body &&
            pick.body !== pick.offerName && (
              <p className="fr-fg-body">
                {pick.body}
              </p>
            )}

          <div className="fr-fg-note">
            ✨ Notification me jo offer
            dekha tha, wahi yahan sabse
            upar hai.
          </div>

          <button
            type="button"
            className="fr-fg-primary"
            onClick={() =>
              goTo(pick.deepLink)
            }
          >
            View Offer
            <span>→</span>
          </button>

          <button
            type="button"
            className="fr-fg-secondary"
            onClick={() =>
              goTo(pick.menuLink)
            }
          >
            <span>▤</span>
            View Menu
          </button>
        </div>
      </div>
    </div>
  );
}

export default FoodRadarForegroundNotification;
